import { SET_AUTHENTICATION_TIMED_OUT, ApplicationDispatch, SET_LOGGED_IN_USER } from "../store/application/types";
import { getClientId } from "./client";
import { fetchConfig } from "../utils/HttpClient";
import {hasValue} from "@amsanalytics/react-shared-library";
import { authService } from "../index";
import { handleIpBlockedError, parseIpBlockError } from "./IpWhitelistHandler";

export async function putData(url = "", data = {}) {
    const headers = {
      Authorization: `Bearer ${await authService.getToken()}`,
      "Content-Type": "application/json",
      ClientId: await getClientId(),
    };
    const config: any = await fetchConfig();
    // console.log('API Endpoint', config.api_endpoint);
    url = `${config.api_endpoint}/${url}`;

    const response = await fetch(url, {
      method: "PUT",
      mode: "cors",
      cache: "no-cache",
      credentials: "same-origin",
      headers,
      redirect: "follow",
      referrer: "no-referrer",
      body: JSON.stringify(data) // body data type must match "Content-Type" header
    });

    const json = await response.json(); // parses JSON response into native JavaScript objects

    if (!response.ok) {
        const msg = `${JSON.stringify(json)}`;
        throw msg;
    } 

    return json;
}
  
export async function postData(url = "", data = {}) {
    const headers = {
      Authorization: `Bearer ${await authService.getToken()}`,
      "Content-Type": "application/json",
      ClientId: await getClientId(),
    };
    const config: any = await fetchConfig();
    // console.log('API Endpoint', config.api_endpoint);
    url = `${config.api_endpoint}/${url}`;

    const response = await fetch(url, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      credentials: "same-origin",
      headers,
      redirect: "follow",
      referrer: "no-referrer",
      body: JSON.stringify(data) // body data type must match "Content-Type" header
    });

    const json = await response.json(); // parses JSON response into native JavaScript objects

    if (!response.ok) {
        const msg = `Unable to post data: ${JSON.stringify(json)}`;
        throw msg;
    } 

    return json;
}

export const postFile = async (url = "", file: File) => {
    
    if(hasValue(url)) {
        const config: any = await fetchConfig();
        // console.log('API Endpoint', config.api_endpoint);
        url = `${config.api_endpoint}/${url}`;
    }
    const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: {
            Authorization: `Bearer ${await authService.getToken()}`,
            "Content-Type": file.type,
            ClientId: await getClientId(),
        },
        body: file,
    });    
    return response;
}
  
export const fetchData = async (url: string, dispatch: ApplicationDispatch) => {
    try {
        const headers = {
            Authorization: `Bearer ${await authService.getToken()}`,
            ClientId: await getClientId(),
        };

        const config: any = await fetchConfig();
        // console.log('API Endpoint', config.api_endpoint);
        url = `${config.api_endpoint}/${url}`;

        const response = await fetch(`${url}`, { headers });
        // const result: any = await response.text();
        if (!response.ok) {
            if (response.status === 401) {
                // Redirect to login if we got an Unauthorized response.
                //preventAccess is used to show the NotAuthorized screen.
                // dispatch({ type: SET_AUTHENTICATION_TIMED_OUT, payload: true });
                return {
                    preventAccess: true
                };
            }
            if (response.status === 403) {
                const json = await response.json().catch(() => null);
                const ipBlockError = parseIpBlockError(json);
                if (ipBlockError) {
                    handleIpBlockedError(dispatch, ipBlockError);
                    throw new Error(JSON.stringify(ipBlockError));
                }
            }
            throw new Error(response.statusText);
        }

        return await response.json();
    } catch (err) {
        const ipBlockError = parseIpBlockError(err);
        if (ipBlockError) {
            throw err;
        }

        throw `${err}`;
    }
};

export const fetchFile = async (url: string, dispatch: ApplicationDispatch) => {
    try {
        const headers = { 
            Authorization: `Bearer ${await authService.getToken()}`,
            ClientId: await getClientId(),
        };

        const config: any = await fetchConfig();
        // console.log('API Endpoint', config.api_endpoint);
        url = `${config.api_endpoint}/${url}`;

        const response = await fetch(`${url}`, { headers });
        if (!response.ok) {
            if (response.status === 401) { // Redirect to login if we got an Unauthorized response.
                dispatch({ type: SET_AUTHENTICATION_TIMED_OUT, payload: true });
            }
            throw new Error(`${response.statusText} - ${JSON.stringify(response.json())}`);
        }

        return response;
    } catch (err) {    
        const msg = `Unable to download file: ${err}`;
        //dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: msg, isError: true } });
        throw `${err}`;
    }        
}

export async function deleteData(url = "") {
    const headers = {
      Authorization: `Bearer ${await authService.getToken()}`,
      "Content-Type": "application/json",
      ClientId: await getClientId(),
    };
    const config: any = await fetchConfig();
    url = `${config.api_endpoint}/${url}`;

    const response = await fetch(url, {
      method: "DELETE",
      mode: "cors",
      cache: "no-cache",
      credentials: "same-origin",
      headers,
      redirect: "follow",
      referrer: "no-referrer",
    });

    const text = await response.text();

    let json;
    try {
      json = JSON.parse(text);
    } catch {
      // Response body is not valid JSON (e.g. plain text or empty on success)
      if (!response.ok) {
        // Non-OK response with non-JSON body: surface the raw text or a generic message
        throw text || `Request failed with status ${response.status}`;
      }

      // Successful response with body that is not valid JSON
      const trimmed = text.trim();
      if (trimmed === "") {
        // Treat empty body (e.g. 204 No Content) as a successful DELETE with no payload
        return undefined as any;
      }

      // Non-empty, non-JSON body on success: return raw text instead of throwing
      return text as any;
    }

    if (!response.ok) {
        const msg = `${JSON.stringify(json)}`;
        throw msg;
    }

    return json;
}
